<template>
    <b-modal
        ref="dialog"
        title-tag="h2"
        :title="title"
        :lazy="true"
        @hidden="close"
        id="modal-problem-dialog"
        no-close-on-backdrop
    >
        <b-form-group label-cols="3" label="Problem:" label-for="problem">
            <b-input
                placeholder="Problem (required)"
                v-model="problem"
                :class="{
                    'border-danger': formError.includes('problem')
                }"
            />
        </b-form-group>

        <b-form-group
            label-cols="3"
            label="Opened:"
            label-for="opened"
            v-if="!isCloseMode"
        >
            <date-time-picker
                id="opened"
                v-model="opened"
                v-validate
                :placeholder="{
                    date: 'Opened (required)',
                    time: 'Time (required)'
                }"
                :inner-class="{
                    'border-danger': errors.has('opened')
                }"
                rules="required"
                name="opened"
                container="#modal-problem-dialog"
                format="datetime"
            />
        </b-form-group>

        <b-form-group
            label-cols="3"
            label="Opened By:"
            label-for="opened by"
            v-if="!isCloseMode"
        >
            <vue-select
                v-model="openedBy"
                label="fullname"
                placeholder="Opened by (required)"
                :disabled="isLoadingListStaff"
                :options="staffOptions"
                :class="{
                    'border-danger': formError.includes('openedBy')
                }"
            >
                <template v-slot:open-indicator>
                    <span></span>
                </template>
            </vue-select>
        </b-form-group>

        <b-form-group label-cols="3" label="Closed:" label-for="closed">
            <date-time-picker
                is-optional
                id="closed"
                v-model="closed"
                v-validate
                :placeholder="{
                    date: `Closed (${
                        ishasdataclosed || isCloseMode ? 'required' : 'optional'
                    })`,
                    time: `Time (${
                        ishasdataclosed || isCloseMode ? 'required' : 'optional'
                    })`
                }"
                :inner-class="{
                    'border-danger':
                        errors.has('closed') || formError.includes('closed')
                }"
                :rules="ishasdataclosed ? 'required' : ''"
                @update-required="
                    value => {
                        ishasdataclosed = value;
                    }
                "
                name="closed"
                container="#modal-problem-dialog"
                format="datetime"
                :key="closedPickerKey"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Closed By:" label-for="closed by">
            <vue-select
                v-model="closedBy"
                label="fullname"
                :placeholder="
                    ishasdataclosed || isCloseMode
                        ? 'Closed by (required)'
                        : 'Closed by'
                "
                :disabled="isLoadingListStaff"
                :options="staffOptions"
                :class="{
                    'border-danger': formError.includes('closedBy')
                }"
            >
                <template v-slot:open-indicator>
                    <span></span>
                </template>
            </vue-select>
        </b-form-group>

        <b-form-group label-cols="3" label="Notes:" label-for="notes">
            <b-textarea placeholder="Notes (optional)" v-model="notes" />
        </b-form-group>

        <template v-slot:modal-footer="{ cancel, close }">
            <loader v-if="isSaving" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="cancel"
                v-if="!isSaving"
            >
                Cancel
            </b-button>

            <b-button-group v-if="!isSaving && !isEditMode && !isCloseMode">
                <b-dropdown
                    v-if="!isContinuous"
                    @click="createNew(false)"
                    left
                    split
                    text="Save + Done"
                    variant="primary"
                >
                    <b-dropdown-item @click="createNew(true)"
                        >Save + Add New</b-dropdown-item
                    >
                </b-dropdown>
                <b-dropdown
                    v-else
                    @click="createNew(true)"
                    left
                    split
                    text="Save + Add New"
                    variant="primary"
                >
                    <b-dropdown-item @click="createNew(false)"
                        >Save + Done</b-dropdown-item
                    >
                </b-dropdown>
            </b-button-group>

            <b-button
                variant="primary"
                @click="saveChanges(close)"
                v-if="!isSaving && (isEditMode || isCloseMode)"
            >
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { DateTime } from "luxon";
import VueSelect from "vue-select";
import Problems from "@/services/problems";
import CONSTANTS from "@/constants/history";
import Loader from "@/components/elements/loader";
import DateTimePicker from "@/module/commons/date-time-picker.vue";

export default {
    name: "ModalProblem",
    components: {
        VueSelect,
        Loader,
        DateTimePicker
    },
    props: {
        patientId: {
            type: String
        }
    },
    data() {
        return {
            title: "",
            problem: "",
            opened: null,
            openedBy: null,
            closed: null,
            closedBy: null,
            notes: "",
            formError: [],
            timeFormat: "hh:mm A",
            isLoadingListStaff: true,
            staffOptions: [],
            isSaving: false,
            isEditMode: false,
            isCloseMode: false,
            editRecordId: null,
            isContinuous: false,
            isDateCarbon: false,
            closedPickerKey: 0,
            ishasdataclosed: false
        };
    },
    computed: {
        staffPlaceholder() {
            return this.isLoadingListStaff
                ? "Loading..."
                : "Select staff member (optional)";
        },
        closeFieldsCompulsory() {
            return this.isCloseMode || this.closed || !_.isEmpty(this.closedBy);
        }
    },
    watch: {
        problem(value) {
            if (value) {
                this.clearError("problem");
            }
        },
        openedBy(value) {
            if (value) {
                this.clearError("openedBy");
            }
        },
        closedTime(value) {
            if (value) {
                this.clearError("closedTime");
            }
        },
        closedBy(value) {
            if (value) {
                this.clearError("closedBy");
            }
        },
        closed(value) {
            if (!(this.isCloseMode && !value)) {
                this.clearError("closed");
            }
        }
    },
    methods: {
        async open(params = {}) {
            let title = "New Problem";

            // if (params.dateCarbon) {
            //     this.isDateCarbon = params.dateCarbon;
            // }

            if (params.action === "edit") {
                title = "Edit Problem";
                this.isEditMode = true;
                this.fetchStaffList(false);

                params.getDetails
                    ? this.getDetails(params.editRecord.id)
                    : this.applyRecord(params.editRecord);
            } else if (params.action === "close") {
                title = "Close Problem";
                this.isCloseMode = true;

                params.getDetails
                    ? this.getDetails(params.editRecord.id)
                    : this.applyRecord(params.editRecord);

                this.fetchStaffList(false);
                this.initClosedFields();
            } else {
                this.initDateTime();
                this.fetchStaffList();
            }

            this.$refs.dialog.show();
            this.title = title;
        },
        close() {
            this.$refs.dialog.hide();
        },
        getDetails(problemId) {
            Problems.getProblem(this.patientId, problemId)
                .then(response => {
                    if (response.message.code == 200) {
                        this.applyRecord(response.resource);
                    } else {
                        this._message(
                            "Failed to get problem's details",
                            "danger"
                        );
                        this.$refs.dialog.hide();
                    }
                })
                .catch(error => {
                    this._message("Failed to get problem's details", "danger");
                    this.$refs.dialog.hide();
                    console.error(error);
                });
        },
        initClosedFields() {
            this.closedBy = this.$root.currentActor;
            this.closed = DateTime.local().setZone(this._timeZone);
        },
        applyRecord(record = {}) {
            this.editRecordId = record.id;
            this.notes = record.note;
            this.problem = record.description;

            if (record.date_closed && record.date_closed !== "") {
                this.closed = this._toDateTime(record.date_closed);
            }

            this.opened = this._toDateTime(record.date_opened);

            if (record.opened_by) {
                record.opened_by.fullname = record.opened_by.full_name;
                this.openedBy = record.opened_by;
            }

            if (record.closed_by) {
                record.closed_by.fullname = record.closed_by.full_name;
                this.closedBy = record.closed_by;
            }
        },
        clearError(key) {
            this.formError = this.formError.filter(err => err !== key);
        },
        formatDate(date, time) {
            let selectedDate = DateTime.fromFormat(
                date + " " + time,
                this._dateFormat + " hh:mm a"
            );
            return selectedDate.toISO({ suppressMilliseconds: true });
        },
        async saveChanges(closeFn) {
            this.formError = [];

            if (!this.problem || this.problem === "") {
                this.formError.push("problem");
            }

            if (!this.opened) {
                this.formError.push("opened");
            }

            if (!this.openedBy) {
                this.formError.push("openedBy");
            }

            if (
                (!this.closed && this.ishasdataclosed) ||
                (this.closed && this.closed < this.opened)
            ) {
                this.formError.push("closed");
            }

            this.closedBy = !Array.isArray(this.closedBy)
                ? this.closedBy
                : this.closedBy.length > 0
                ? this.closedBy
                : null;

            if (!this.closedBy && this.ishasdataclosed) {
                this.formError.push("closedBy");
            }

            if (this.closeFieldsCompulsory) {
                if (!this.closed || this.closed === "") {
                    this.formError.push("closed");
                }

                if (!this.closedBy) {
                    this.formError.push("closedBy");
                }
            }

            if (this.formError.length > 0) {
                return;
            }

            let opened_by_id = this.openedBy ? this.openedBy.id : "";
            let opened_by = this.openedBy ? this.openedBy.fullname : "";
            let closed_by_id = this.closedBy ? this.closedBy.id : "";
            let closed_by = this.closedBy ? this.closedBy.fullname : "";
            let date_opened = Number(this.opened.toFormat("X")); // to timestamp
            let time_opened = this.opened.toFormat("hh:mm a");
            let date_closed = "";
            let time_closed = "";

            if (this.closed) {
                date_closed = Number(this.closed.toFormat("X"));
                time_closed = this.opened.toFormat("hh:mm a");
            }

            this.isSaving = true;
            let params = {
                problem_id: this.editRecordId,
                referrer: "patient",
                patient_id: this.patientId,
                opened_by_id,
                closed_by_id,
                description: this.problem,
                date_opened,
                time_opened,
                opened_by: opened_by,
                date_closed,
                time_closed,
                closed_by,
                note: this.notes
            };

            Problems.updateProblem(this.editRecordId, params)
                .then(response => {
                    if (response.message.code === 200) {
                        this._message("Edits saved successfully");
                        this.$emit("reload");

                        closeFn();
                    } else {
                        this._message(
                            "Error : Unable to update problem",
                            "danger"
                        );
                    }

                    this.isSaving = false;
                })
                .catch(error => {
                    this._message("Error : Unable to update problem", "danger");
                    this.isSaving = false;
                    console.error(error);
                });
        },
        async createNew(reset = false) {
            this.formError = [];

            if (!this.problem || this.problem === "") {
                this.formError.push("problem");
            }

            if (!this.opened) {
                this.formError.push("opened");
            }

            if (!this.openedBy) {
                this.formError.push("openedBy");
            }

            if (
                (!this.closed && this.ishasdataclosed) ||
                (this.closed && this.closed < this.opened)
            ) {
                this.formError.push("closed");
            }

            if (!this.closedBy && this.ishasdataclosed) {
                this.formError.push("closedBy");
            }

            if (this.formError.length > 0) {
                return;
            }

            let opened_by_id = this.openedBy ? this.openedBy.id : "";
            let opened_by = this.openedBy ? this.openedBy.fullname : "";
            let closed_by_id = this.closedBy ? this.closedBy.id : "";
            let closed_by = this.closedBy ? this.closedBy.fullname : "";
            let date_opened = Number(this.opened.toFormat("X")); // to timestamp
            let time_opened = this.opened.toFormat("hh:mm a");
            let date_closed = "";
            let time_closed = "";

            if (this.closed) {
                date_closed = Number(this.closed.toFormat("X"));
                time_closed = this.opened.toFormat("hh:mm a");
            }

            this.isSaving = true;
            let params = {
                referrer: "patient",
                patient_id: this.patientId,
                opened_by_id,
                closed_by_id,
                description: this.problem,
                date_opened,
                time_opened: time_opened,
                opened_by: opened_by,
                date_closed,
                time_closed,
                closed_by,
                note: this.notes
            };

            Problems.saveProblem(this.patientId, params)
                .then(response => {
                    if (response.message.code === 200) {
                        this._message("Problem successfully added");

                        if (reset) {
                            this.isContinuous = reset;

                            this.problem = "";
                            this.openedBy = this.$root.currentActor;
                            this.closed = null;
                            this.closedBy = null;
                            this.notes = "";
                            this.formError = [];
                            this.ishasdataclosed = false;

                            this.initDateTime();
                            this.closedPickerKey++;
                        } else {
                            this.$refs.dialog.hide();
                            this.isContinuous = false;
                        }

                        this.$emit("reload", CONSTANTS.VIEWS.PROBLEMS);
                    } else {
                        this._message(
                            "Error : Unable to add problem",
                            "danger"
                        );
                    }

                    this.isSaving = false;
                })
                .catch(error => {
                    this._message("Error : Unable to add problem", "danger");
                    this.isSaving = false;
                    console.error(error);
                });
        },
        initDateTime() {
            this.opened = DateTime.local().setZone(this._timeZone);
        },
        async fetchStaffList(isSetDefault = true) {
            try {
                this.isLoadingListStaff = true;
                const res = await this.$http.get(
                    `${process.env.BARRAMUNDI}actor/staff`
                );

                const response = res.data.response;

                if (
                    response &&
                    response.message &&
                    response.message.code === 200
                ) {
                    this.staffOptions = response.resources || [];
                    this.isLoadingListStaff = false;

                    if (isSetDefault) {
                        this.openedBy = this.$root.currentActor;
                    }
                } else {
                    throw new Error("Unable to get staff list");
                }
            } catch (e) {
                this.isLoadingListStaff = false;
                this.staffOptions = [];
            }
        }
    }
};
</script>
